import { action, computed, makeObservable, observable } from 'mobx'
import { EventEmitter } from '../../util/EventEmitter'
import { ServerIdentities } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'
import { DisposerTracker } from '../../util/DisposerTracker'
import { roomStateUpdatePumpSlideWithActiveSlideChangedAt } from '../../firestore/RoomState'

// How long the client waits for the Secretary to appear before it logs a
// timeout and starts pumping the slide to force a respawn.
export const SECRETARY_INIT_TIMEOUT_MS = 60_000

// How often the group leader pumps the slide while the Secretary is missing.
export const SECRETARY_RECONNECT_INTERVAL_MS = 15_000

export type SecretaryConnectionState =
  | 'initializing'
  | 'connected'
  | 'disconnected'

export type SecretaryConnectionEvents = {
  connected: []
  disconnected: []
  reconnected: []
  timeout: []
}

// generateAttemptId returns an id that ties together every telemetry event for
// one Secretary connection attempt (one room join in one tab). It returns a
// full UUID rather than a truncated one: the id is a join key across many
// controller instances, and 8 hex chars (32 bits) collide at roughly a coin
// flip by ~77k instances, which would merge unrelated attempts. It falls back
// to a timestamp-based id where crypto.randomUUID is unavailable.
function generateAttemptId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Tracks whether the Secretary (the server-side transcription participant) is
 * present in the LiveKit room and reports its lifecycle to telemetry.
 *
 * Every event carries `secretary_attempt_id` so a `meeting_secretary_timeout`
 * can be joined to the `meeting_secretary_connected` that follows it. Without
 * that join the raw timeout count is misleading: most timeouts recover a few
 * seconds later and transcription still succeeds. See
 * docs/systems/telemetry/secretary.md.
 */
export class SecretaryConnectionController {
  @observable
  state: SecretaryConnectionState = 'initializing'

  // Stable id for this controller's whole lifecycle (one room join, one tab).
  readonly attemptId = generateAttemptId()

  private disposers = new DisposerTracker()

  private reconnectInterval: ReturnType<typeof setInterval> | null = null
  private initTimeout: ReturnType<typeof setTimeout> | null = null

  // Timing state used to enrich lifecycle events. All are epoch milliseconds
  // except retryCount (a count of slide pumps so far).
  private connectStartedAt = Date.now()
  private connectedAt: number | null = null
  private timedOut = false
  private timedOutAt: number | null = null
  private retryCount = 0
  private initialized = false

  events = new EventEmitter<SecretaryConnectionEvents>()

  constructor(private meeting: MeetingCubit) {
    makeObservable(this)
  }

  initialize() {
    if (this.doesNotNeedSecretary) return
    // Only the first initialize of an attempt starts the clock. Guard against a
    // repeat call (state stays 'initializing' until the Secretary appears)
    // re-stamping connectStartedAt and firing a spurious connect_started, which
    // would skew elapsed_ms on later events.
    if (this.initialized) return
    this.initialized = true
    this.connectStartedAt = Date.now()
    this.meeting.logEvent('meeting_secretary_connect_started', {
      secretary_attempt_id: this.attemptId,
      deadline_ms: SECRETARY_INIT_TIMEOUT_MS,
    })
    this.startInitTimeout()
  }

  @computed
  get doesNotNeedSecretary(): boolean {
    return this.meeting.isPreview || this.meeting.isDemo
  }

  @computed
  get isSecretaryMissing(): boolean {
    if (this.doesNotNeedSecretary) return false
    return this.state !== 'connected'
  }

  @computed
  get isSecretaryConnected(): boolean {
    if (this.doesNotNeedSecretary) return true
    return this.state === 'connected'
  }

  @computed
  get isSecretaryDisconnected(): boolean {
    if (this.doesNotNeedSecretary) return false
    return this.state === 'disconnected'
  }

  @action
  handleParticipantIdsUpdate(participantIds: string[]) {
    if (this.doesNotNeedSecretary) return

    const secretaryPresent = participantIds.includes(ServerIdentities.Secretary)

    if (secretaryPresent) {
      if (this.state === 'initializing') {
        this.state = 'connected'
        this.stopInitTimeout()
        this.logConnected()
        this.events.emit('connected')
      } else if (this.state === 'disconnected') {
        this.state = 'connected'
        this.logConnected()
        this.stopReconnectInterval()
        this.events.emit('reconnected')
      }
    } else {
      if (this.state === 'connected') {
        this.state = 'disconnected'
        this.logDisconnected()
        this.startReconnectInterval()
        this.events.emit('disconnected')
      }
    }
  }

  // logConnected records a successful connect and, when it follows a timeout,
  // labels it as a recovery so the earlier timeout can be classed as harmless.
  private logConnected() {
    const now = Date.now()
    this.connectedAt = now
    const params: Record<string, unknown> = {
      secretary_attempt_id: this.attemptId,
      elapsed_ms: now - this.connectStartedAt,
      retry_ordinal: this.retryCount,
      recovered_from_timeout: this.timedOut,
    }
    if (this.timedOut && this.timedOutAt !== null) {
      params.ms_since_timeout = now - this.timedOutAt
    }
    this.meeting.logEvent('meeting_secretary_connected', params)
    // A later reconnect in this same attempt is a plain reconnect, not a
    // timeout recovery, so clear the timeout marker once we have recovered.
    this.timedOut = false
    this.timedOutAt = null
  }

  private logDisconnected() {
    const now = Date.now()
    const params: Record<string, unknown> = {
      secretary_attempt_id: this.attemptId,
    }
    if (this.connectedAt !== null) {
      params.connected_duration_ms = now - this.connectedAt
    }
    this.meeting.logEvent('meeting_secretary_disconnected', params)
    // Restart the wait clock so elapsed_ms on the next connect measures the
    // outage (time the Secretary was gone), not the time since room join. A
    // plain reconnect after a long healthy session would otherwise report an
    // elapsed_ms of minutes and read like a very slow connect.
    this.connectStartedAt = now
  }

  private startInitTimeout() {
    if (this.doesNotNeedSecretary) return
    if (this.state !== 'initializing') return
    if (this.initTimeout) return

    this.initTimeout = setTimeout(() => {
      if (this.state !== 'initializing') return
      this.handleInitTimeout()
    }, SECRETARY_INIT_TIMEOUT_MS)
  }

  @action
  private handleInitTimeout() {
    this.state = 'disconnected'
    this.timedOut = true
    this.timedOutAt = Date.now()
    this.meeting.logEvent('meeting_secretary_timeout', {
      secretary_attempt_id: this.attemptId,
      elapsed_ms: this.timedOutAt - this.connectStartedAt,
      deadline_ms: SECRETARY_INIT_TIMEOUT_MS,
    })
    this.startReconnectInterval()
    this.events.emit('timeout')
  }

  private stopInitTimeout() {
    if (this.initTimeout) {
      clearTimeout(this.initTimeout)
      this.initTimeout = null
    }
  }

  private startReconnectInterval() {
    if (this.meeting.isPreview || this.meeting.isDemo) return
    if (!this.meeting.currentUserIsGroupLeader) return
    if (this.reconnectInterval) return

    this.reconnectInterval = setInterval(() => {
      if (this.state === 'connected') {
        this.stopReconnectInterval()
        return
      }
      this.pumpSlideWithActiveSlideTouch()
    }, SECRETARY_RECONNECT_INTERVAL_MS)
  }

  private stopReconnectInterval() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval)
      this.reconnectInterval = null
    }
  }

  private pumpSlideWithActiveSlideTouch() {
    if (!this.meeting.currentUserIsGroupLeader) return
    this.retryCount += 1
    this.meeting.logEvent('pump_slide_with_active_slide_touch', {
      slide_index: this.meeting.activeSlide,
      secretary_attempt_id: this.attemptId,
      retry_ordinal: this.retryCount,
    })
    roomStateUpdatePumpSlideWithActiveSlideChangedAt(this.meeting.firestore, {
      roomId: this.meeting.roomId,
    }).catch(() => {
      // Silently ignore — the interval will retry
    })
  }

  dispose() {
    this.stopInitTimeout()
    this.stopReconnectInterval()
    this.disposers.dispose()
  }
}
